import { useEffect } from 'react';
import { PageShell } from '@/components/layout/PageShell';
import { SectionMarker } from '@/components/brand/SectionMarker';
import { Icon } from '@/components/ui/Icon';
import { SignalPill } from '@/components/ui/SignalPill';
import { capture } from '@/modules/heat/signals';
import type { WorldSummary } from '../types';
import { cn } from '@/lib/cn';

const MOCK_SUMMARY: WorldSummary = {
  hottrId: 'hid_8f4a-22cd-9e10',
  handle: 'kasper',
  links: [
    { surface: 'app', label: 'HOTTR.', status: 'live' },
    { surface: 'world', label: 'HOTTR.world', status: 'soon' },
    /* .vip joins this list when its backend is real — the same gate as
       LIVE_SURFACES in config/routes. Until then it is not offered, and not
       linked: there is nowhere for it to go. */
  ],
};

const STATUS_COPY: Record<'live' | 'locked' | 'soon', string> = {
  live: 'Connected',
  locked: 'Locked',
  soon: 'Soon',
};

const SURFACE_LINE: Record<'app' | 'world' | 'vip', string> = {
  app: 'You are here.',
  world: 'Identity layer is the spine. Not a destination.',
  vip: 'Directory module. Preview from the app.',
};

/**
 * World — small identity / control layer. Restrained editorial card.
 * Not a dashboard. Not a hub.
 */
export const WorldPage = () => {
  useEffect(() => {
    capture('surface_enter', 'world');
  }, []);

  return (
    <PageShell
      rail={false}
      mobileTopBar={
        <>
          {/* Identity is the WorldSwitcher in PageShell's bar — one lockup.
              This spacer keeps the page label on the right edge. */}
          <span aria-hidden />
          <span className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            World
          </span>
        </>
      }
    >
      {/* HEADER */}
      <section className="border-b border-white/[0.06] px-5 py-7 md:px-12 md:py-12">
        <SectionMarker className="mb-4">Identity · the spine</SectionMarker>
        <h1
          className="font-display font-black uppercase tracking-display"
          style={{
            fontSize: 'clamp(36px, 6vw, 92px)',
            lineHeight: 0.9,
            letterSpacing: '-0.04em',
          }}
        >
          One profile<span className="text-pink">.</span>
          <br />
          <span className="font-light italic text-text-secondary">Every version of you</span>
          <span className="text-pink">.</span>
        </h1>
      </section>

      <div className="mx-auto grid max-w-5xl gap-8 px-5 py-10 md:grid-cols-[1fr_minmax(0,1.4fr)] md:px-12 md:py-14 md:gap-10">
        {/* HOTTR ID card */}
        <section>
          <SectionMarker className="mb-4">HOTTR ID</SectionMarker>
          <div
            className={cn(
              'relative border border-white/[0.10] bg-charcoal/40 p-7',
              'shadow-[inset_0_0_60px_rgba(255,12,182,0.04)]',
            )}
          >
            <p className="font-mono text-[13px] text-text-secondary break-all">
              {MOCK_SUMMARY.hottrId}
            </p>
            <div className="mt-7 flex items-center justify-between border-t border-white/[0.07] pt-6">
              <div>
                <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
                  Handle
                </p>
                <p className="mt-1 font-display text-3xl font-black uppercase tracking-display">
                  @{MOCK_SUMMARY.handle}<span className="text-pink">.</span>
                </p>
              </div>
              <span className="flex h-12 w-12 items-center justify-center border border-pink/40 text-pink">
                <Icon name="fingerprint" filled size={22} />
              </span>
            </div>
            <p className="mt-5 font-display text-[10px] font-bold uppercase italic tracking-widest text-text-muted">
              Pseudonymous<span className="text-pink">.</span> Never a phone or email<span className="text-pink">.</span>
            </p>
          </div>
        </section>

        {/* Linked surfaces */}
        <section>
          <SectionMarker className="mb-4" muted>
            Linked surfaces
          </SectionMarker>
          <ul className="divide-y divide-white/[0.07] border-y border-white/[0.07]">
            {MOCK_SUMMARY.links.map((link) => (
              <li
                key={link.surface}
                className="flex items-center justify-between gap-4 py-5"
              >
                <div className="min-w-0">
                  <p className="font-display text-base font-bold uppercase tracking-wide text-text-primary md:text-lg">
                    {link.label.replace('.', '')}
                    <span className="text-pink">.</span>
                    <span className="font-light italic text-text-secondary">
                      {link.label.split('.')[1]}
                    </span>
                  </p>
                  <p
                    className={cn(
                      'mt-1 text-xs',
                      link.status === 'live' ? 'text-text-secondary' : 'text-text-muted',
                    )}
                  >
                    {SURFACE_LINE[link.surface]}
                  </p>
                </div>
                <SignalPill band={link.status === 'live' ? 'pulse' : 'cool'}>
                  {STATUS_COPY[link.status]}
                </SignalPill>
              </li>
            ))}
          </ul>
          <p className="mt-7 font-display text-[10px] font-bold uppercase italic tracking-widest text-text-muted">
            One sign-on<span className="text-pink">.</span> Every room<span className="text-pink">.</span>
          </p>
        </section>
      </div>
    </PageShell>
  );
};
